import dayjs, { Dayjs } from 'dayjs';

import {
  MANUAL_SLEEP_DATA_DEVICE_NAME,
  SleepWakeTimeISOStringSet,
  useAuthContext,
  useUpdateSleepDetails,
  useUpdateSleepPrediction,
} from '../data-access';
import { trackEventMixpanel } from '../utils';

interface UseEditSleepParams {
  date: Dayjs;
  skipAutoSuggestion?: boolean;
  onAfterEdit?: () => void;
}

interface EditSleepPredictionParams {
  newData: SleepWakeTimeISOStringSet;
  shouldUpdateEvent?: boolean;
  editedManually?: boolean;
  description?: string;
  baseCalendarId?: string;
}

export const useEditSleep = ({
  date,
  skipAutoSuggestion = false,
  onAfterEdit,
}: UseEditSleepParams) => {
  const { updateSleepDetail, isPending: isEditingData } = useUpdateSleepDetails();
  const { updateSleepPrediction, isPending: isUpdatingSleepPrediction } =
    useUpdateSleepPrediction();
  const { user } = useAuthContext();

  const canSubmitSleepEdit = Boolean(user.metricId) && !isEditingData;
  const shouldRerunAutoSuggestion = (targetDate: string) =>
    dayjs(targetDate).isSame(dayjs(), 'day');

  const handleEditSleepData = async (newData: SleepWakeTimeISOStringSet) => {
    const metricId = user.metricId;
    if (!metricId) {
      console.error('Unable to update sleep details without a metricId.');
      return;
    }
    const targetDate = dayjs(newData.wakeTime).format('YYYY-MM-DD');

    await updateSleepDetail({
      userId: user.userId,
      metricId,
      detailsByDate: [
        {
          date: targetDate,
          details: {
            sleepTime: dayjs(newData.sleepTime).format('YYYY-MM-DDTHH:mm:ss'),
            awakeTime: dayjs(newData.wakeTime).format('YYYY-MM-DDTHH:mm:ss'),
            durationMin: dayjs(newData.wakeTime).diff(dayjs(newData.sleepTime), 'minutes'),
            source: MANUAL_SLEEP_DATA_DEVICE_NAME,
            stages: {
              deep: 0,
              light: 0,
              rem: 0,
              awake: 0,
            },
          },
        },
      ],
    });
    trackEventMixpanel('edit_sleep_data', { ...newData });
    if (!skipAutoSuggestion && shouldRerunAutoSuggestion(targetDate)) {
      onAfterEdit?.();
    }
  };

  const handleEditSleepPrediction = async ({
    newData,
    shouldUpdateEvent = true,
    editedManually = true,
    description = '',
    baseCalendarId = '',
  }: EditSleepPredictionParams) => {
    const metricId = user.metricId;
    if (!metricId) {
      console.error('Unable to update sleep prediction without a metricId.');
      return;
    }
    const targetDate = dayjs(newData.wakeTime).format('YYYY-MM-DD');

    await updateSleepPrediction({
      metricId,
      date: targetDate,
      prediction: {
        sleepTime: dayjs(newData.sleepTime).format('HH:mm'),
        wakeTime: dayjs(newData.wakeTime).format('HH:mm'),
        description,
      },
      editedManually,
      shouldUpdateEvent,
      baseCalendarId,
    });
    trackEventMixpanel('edit_sleep_prediction', { ...newData });
    if (!skipAutoSuggestion && shouldRerunAutoSuggestion(targetDate)) {
      onAfterEdit?.();
    }
  };

  return {
    handleEditSleepData,
    handleEditSleepPrediction,
    isEditingData,
    isUpdatingSleepPrediction,
    canSubmitSleepEdit,
  };
};
